import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";
import { Button } from "@/components/ui/button";

const STORAGE_KEY = "credanovelia_consent_v2";

/**
 * Sets the Google Consent Mode v2 signals via gtag('consent', 'update', ...).
 */
const applyConsent = (accepted) => {
  const granted = accepted ? "granted" : "denied";
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      ad_storage: granted,
      analytics_storage: granted,
      ad_user_data: granted,
      ad_personalization: granted,
      functionality_storage: "granted",
      security_storage: "granted",
    });
  }
};

export default function CookieBanner() {
  const { t, lang } = useI18n();
  const [visible, setVisible] = useState(false);

  const cookiesPath = lang === "mx" ? "/mx/cookies" : "/en/cookies";

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) setVisible(true);
      else if (stored === "accepted") applyConsent(true);
      else applyConsent(false);
    } catch (e) {
      setVisible(true);
    }
  }, []);

  const handleAccept = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "accepted");
    } catch (e) {
      /* noop */
    }
    applyConsent(true);
    setVisible(false);
  };

  const handleReject = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "rejected");
    } catch (e) {
      /* noop */
    }
    applyConsent(false);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      role="dialog"
      aria-label={t("cookie.title")}
      className="fixed inset-x-0 bottom-0 z-50 border-t border-slate-200 bg-white shadow-2xl"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold text-slate-900">
              {t("cookie.title")}
            </p>
            <p className="mt-1 text-sm text-slate-600 leading-relaxed">
              {t("cookie.message")}{" "}
              <Link
                to={cookiesPath}
                data-testid="cookie-banner-learn"
                className="font-semibold text-[#059669] underline underline-offset-2"
              >
                {t("cookie.learn")}
              </Link>
            </p>
          </div>
          <div className="flex flex-wrap gap-3 lg:justify-end">
            <Button
              data-testid="cookie-reject"
              variant="outline"
              onClick={handleReject}
              className="rounded-full border-slate-300 text-slate-700 hover:bg-slate-100"
            >
              {t("cookie.reject")}
            </Button>
            <Button
              data-testid="cookie-accept"
              onClick={handleAccept}
              className="rounded-full bg-[#059669] hover:bg-[#047857] text-white"
            >
              {t("cookie.accept")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
